import React, { useEffect, useState } from 'react';
import { Modal, Input, Select, DatePicker, Upload, Cascader, App } from 'antd';
import type { UploadFile, UploadProps } from 'antd';
import styles from './index.module.scss';
import { useAppSelector } from '@/store/hooks';
import { getProductCategoryAllList } from '@/api/products';

type EmergencyOrderEditDialogProps = {
  open: boolean;
  onCancel: () => void;
};

// 纯静态编辑弹窗（复用发布商品的分类级联与上传交互）
const EmergencyOrderEditDialog: React.FC<EmergencyOrderEditDialogProps> = ({ open, onCancel }) => {
  const { message } = App.useApp();
  const token = useAppSelector((state) => state.user.token);

  // 分类级联数据
  type CascaderOption = { value: number; label: string; children?: CascaderOption[]; isLeaf?: boolean };
  type IClassification = { product_category_id: number; product_category_name: string; child?: IClassification[] };
  const [cascaderOptions, setCascaderOptions] = useState<CascaderOption[]>([]);

  const transformToOptions = (data: IClassification[] = []): CascaderOption[] => {
    return data.map(item => {
      const hasChildren = item.child && item.child.length > 0;
      return {
        value: item.product_category_id,
        label: item.product_category_name,
        children: hasChildren ? transformToOptions(item.child!) : undefined,
        isLeaf: !hasChildren
      };
    });
  };

  useEffect(() => {
    if (typeof getProductCategoryAllList === 'function') {
      getProductCategoryAllList(2).then((res: any) => {
        if (res?.code === 200) setCascaderOptions(transformToOptions(res.data || []));
      }).catch(() => {});
    }
  }, []);

  // 图片上传配置
  type FileType = Parameters<NonNullable<UploadProps['beforeUpload']>>[0];
  const [fileList, setFileList] = useState<UploadFile<any>[]>([]);
  // 自定义预览弹窗取消

  const uploadProps: UploadProps = {
    name: 'file',
    action: `${process.env.REACT_APP_API_URL}/api/product/save_upload_file`,
    accept: '.png,.jpeg,.jpg,.gif',
    headers: { token: `${token || ''}` },
    listType: 'picture-card',
    maxCount: 5,
    showUploadList: { showPreviewIcon: false },
    fileList: fileList as any,
    beforeUpload: (file) => {
      const isLt10M = file.size / 1024 / 1024 < 10;
      if (!isLt10M) {
        message.error("Image must be smaller than 10MB!");
      }
      return isLt10M; // 返回 false 阻止上传
    },
    onChange(info) {
      const newFileList = [...info.fileList];
      newFileList.forEach((file: any) => { if (file.response) file.url = file?.response?.data?.url; });
      setFileList(newFileList as any);
      if (info.file.status === 'error') message.error(`${info.file.name} file upload failed.`);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      footer={null}
      width={920}
      className={styles.dialog}
      closable
    >
      <div className={styles.container}>
        <div className={styles.title}>Order Details</div>

        <div className={styles.grid}>
          <div className={styles.formItem}>
            <label className={styles.required}>Service Category</label>
            <Cascader
              className={styles.input}
              options={cascaderOptions}
              expandTrigger="hover"
              placeholder="Select a category"
              showSearch={{
                filter: (input, path) => path.some(option => String(option.label).toLowerCase().includes(input.toLowerCase())),
                matchInputWidth: true,
                limit: 10
              }}
            />
          </div>

          <div className={styles.formItem}>
            <label className={styles.required}>Enter Budget (E.G., $100)</label>
            <Input className={styles.input} value="100" />
          </div>

          <div className={styles.formItem}>
            <label className={styles.required}>Bidding Mechanism</label>
            <Input className={styles.input} value="Optimal Price" />
          </div>

          <div className={styles.formItem}>
            <label className={styles.required}>City/State</label>
            <Select
              className={styles.input}
              placeholder="City/State"
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                (option?.children as unknown as string).toLowerCase().includes(input.toLowerCase())
              }
            >
              {useAppSelector((state) => state.common.StateList)?.map((item: any, index: number) => (
                <Select.Option key={index} value={item.value}>{item.label}</Select.Option>
              ))}
            </Select>
          </div>

          <div className={styles.formItem}>
            <label className={styles.required}>Address</label>
            <Input className={styles.input} value="123 Innovationdrive,Innovation" />
          </div>

          <div className={styles.formItem}>
            <label className={styles.required}>Phone</label>
            <Input className={styles.input} value="(315) 000-1234" />
          </div>

          <div className={styles.formItem}>
            <label className={styles.required}>Appointment Time</label>
            <DatePicker 
                  showTime={{ format: 'HH:mm' }}
                  format="YYYY-MM-DD HH:mm:ss"
                  placeholder="Please Select An Appointment Time"
                  style={{ width: '100%' }}
                />
          </div>

          <div className={styles.formItemFull}>
            <label className={styles.required}>Image Upload</label>
            <div className={styles.uploadRow}>
              <Upload {...uploadProps}>Upload</Upload>
            </div>
          </div>
        </div>

        <div className={styles.footer}>
          <button className={styles.cancelBtn} onClick={onCancel}>Cancel</button>
          <button className={styles.releaseBtn}>Release</button>
        </div>
      </div>

    </Modal>
  );
};

export default EmergencyOrderEditDialog;


